import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useLocation, useNavigate } from "react-router-dom"

import usePermissions from "api/usePermissions"
import { useUserDetails } from "api/useUserDetails"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { URL_ARRAY_PARAM_SEPARATOR, usePaginationFromURL } from "hooks/usePaginationFromURL"
import { permissionsUtil } from "utils/permissionsUtil"

import { OrderFilterKey } from "./constant"
import { OrderFilters } from "./types"
import { getDefaultSelectedProjects, resetQueryParams, updateQueryParam } from "./utils"

dayjs.extend(utc)

export const useOrdersFilters = () => {
    const navigate = useNavigate()
    const location = useLocation()

    const { getPermissions } = usePermissions()
    const permissionsRef = useRef(getPermissions())
    const allowedPublishers = useMemo(
        () => permissionsUtil.getAccessPublisherOrders(permissionsRef.current),
        []
    )

    const { userDetails } = useUserDetails();
    const defaultProjects = useMemo(
        () => getDefaultSelectedProjects(allowedPublishers, userDetails),
        [allowedPublishers, userDetails]
    )

    const defaultEndDate = useMemo(() => dayjs(), [])
    const defaultStartDate = useMemo(() => dayjs().subtract(30, 'days'), [])

    const urlParams = usePaginationFromURL('orders')
    const {
        statuses,
        publisherPurchaseId,
        playerId,
        orderId,
        receiptId,
        subscriptionId,
        projects,
        transactionId,
        startDate: startDateParam,
        endDate: endDateParam,
        isFree: isFreeParam,
        playerEmail,
    } = urlParams

    const [filters, setFilters] = useState<OrderFilters>({
        playerEmail,
        statuses: urlParams.statuses || [],
        isFree: isFreeParam ? isFreeParam === 'true' : false,
        startDate: startDateParam || defaultStartDate.startOf('day').toISOString(),
        endDate: endDateParam || defaultEndDate.endOf('day').toISOString(),
        selectedProjects: defaultProjects.join(','),
        orderId,
        playerId,
        publisherPurchaseId,
        receiptId,
        subscriptionId,
        transactionId,
    })

    const [selectedProjects, setSelectedProjects] = useState<string[]>(
        projects && projects.length > 0
            ? projects.flatMap(p => p.split(URL_ARRAY_PARAM_SEPARATOR))
            : []
    )

    const [rangeDates, setRangeDates] = useState([
        defaultStartDate,
        defaultEndDate
    ])
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
    const [formattedRangeDates, setFormattedRangeDates] = useState<string[] | null[]>([
        null,
        null
    ])

    const formattedDate = useMemo(
        () => `${rangeDates[0].format('DD/MM/YY')} - ${rangeDates[1].format('DD/MM/YY')}`,
        [rangeDates]
    )

    const expandedStatuses = useMemo(() => urlParams.statuses.flatMap(status => status.split(';')), [urlParams.statuses])

    useEffect(() => {
        setFilters((prev) => {
            const newStatuses = expandedStatuses;
            const prevStatuses = prev.statuses || [];
            // Compare array contents, not references
            const statusesChanged =
                prevStatuses.length !== newStatuses.length ||
                prevStatuses.some((status, index) => status !== newStatuses[index]);

            if (statusesChanged) {
                return { ...prev, statuses: newStatuses };
            }
            return prev;
        });
    }, [expandedStatuses])

    useEffect(() => {
        setFilters((prev) => {
            // Only update if any value actually changed
            if (
                prev.orderId !== orderId ||
                prev.playerId !== playerId ||
                prev.publisherPurchaseId !== publisherPurchaseId ||
                prev.receiptId !== receiptId ||
                prev.subscriptionId !== subscriptionId ||
                prev.transactionId !== transactionId ||
                prev.playerEmail !== playerEmail
            ) {
                return {
                    ...prev,
                    orderId,
                    playerId,
                    publisherPurchaseId,
                    receiptId,
                    subscriptionId,
                    transactionId,
                    playerEmail,
                };
            }
            return prev;
        });
    }, [orderId, playerId, publisherPurchaseId, receiptId, subscriptionId, transactionId, playerEmail])

    useEffect(() => {
        setFilters((prev) => {
            // If URL param is empty string (cleared), use default; if defined use it; otherwise keep previous
            const newStartDate = startDateParam !== undefined
                ? (startDateParam || defaultStartDate.startOf('day').toISOString())
                : prev.startDate;
            const newEndDate = endDateParam !== undefined
                ? (endDateParam || defaultEndDate.endOf('day').toISOString())
                : prev.endDate;

            // Only update if values actually changed
            if (prev.startDate !== newStartDate || prev.endDate !== newEndDate) {
                return {
                    ...prev,
                    startDate: newStartDate,
                    endDate: newEndDate
                };
            }
            return prev;
        });
    }, [startDateParam, endDateParam, defaultStartDate, defaultEndDate])

    useEffect(() => {
        setFilters((prev) => {
            const newIsFree = isFreeParam ? isFreeParam === 'true' : false;
            // Only update if value actually changed
            if (prev.isFree !== newIsFree) {
                return { ...prev, isFree: newIsFree };
            }
            return prev;
        });
    }, [isFreeParam])

    const clearFiltersButton = useMemo(() => !!(
        playerId ||
        publisherPurchaseId ||
        receiptId ||
        orderId ||
        subscriptionId ||
        transactionId ||
        statuses.length > 0 ||
        selectedProjects.length > 0 ||
        startDateParam ||
        endDateParam
    ), [playerId, publisherPurchaseId, receiptId, orderId, subscriptionId, transactionId, statuses, selectedProjects, startDateParam, endDateParam])

    const formattedRangeDatesKey = useMemo(
        () => `${formattedRangeDates[0]}_${formattedRangeDates[1]}`,
        [formattedRangeDates]
    )

    useEffect(() => {
        if (formattedRangeDates[0] && formattedRangeDates[1]) {
            setFilters((prev) => {
                const newStartDate = formattedRangeDates[0] as string;
                const newEndDate = formattedRangeDates[1] as string;
                // Only update if values actually changed
                if (prev.startDate !== newStartDate || prev.endDate !== newEndDate) {
                    return { ...prev, startDate: newStartDate, endDate: newEndDate };
                }
                return prev;
            });
        }
    }, [formattedRangeDatesKey, formattedRangeDates])

    const selectedProjectsKey = useMemo(() => selectedProjects.join(','), [selectedProjects])
    const defaultProjectsKey = useMemo(() => defaultProjects.join(','), [defaultProjects])

    useEffect(() => {
        setFilters((prev) => {
            const newSelectedProjects = selectedProjectsKey || defaultProjectsKey;
            if (prev.selectedProjects !== newSelectedProjects) {
                return { ...prev, selectedProjects: newSelectedProjects };
            }
            return prev;
        });
    }, [selectedProjectsKey, defaultProjectsKey])

    useEffect(() => {
        const currentProjectsParam = new URLSearchParams(locationRef.current.search).get('projects') || ''
        const newProjectsValue = selectedProjectsKey || ''

        if (currentProjectsParam !== newProjectsValue) {
            updateQueryParam('projects', newProjectsValue, locationRef.current, navigateRef.current)
        }
    }, [selectedProjectsKey])

    // Initialize date params in URL if not present
    useEffect(() => {
        const queryParams = new URLSearchParams(locationRef.current.search)
        let needsUpdate = false

        // Set default start date if not in URL
        if (!startDateParam && !queryParams.has(OrderFilterKey.START_DATE)) {
            queryParams.set(OrderFilterKey.START_DATE, defaultStartDate.startOf('day').toISOString())
            needsUpdate = true
        }

        // Set default end date if not in URL
        if (!endDateParam && !queryParams.has(OrderFilterKey.END_DATE)) {
            queryParams.set(OrderFilterKey.END_DATE, defaultEndDate.endOf('day').toISOString())
            needsUpdate = true
        }

        if (needsUpdate) {
            navigateRef.current(`${locationRef.current.pathname}?${queryParams.toString()}`, { replace: true })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Run only once on mount to initialize default dates

    const changeFilters = useCallback((newFilters: Partial<OrderFilters>) => {
        setFilters((prev) => ({ ...prev, ...newFilters }))
    }, [])

    const locationRef = useRef(location)
    const navigateRef = useRef(navigate)

    locationRef.current = location
    navigateRef.current = navigate

    const handleUpdateParam = useCallback((paramName: string, paramValue: string) => {
        updateQueryParam(paramName, paramValue, locationRef.current, navigateRef.current)
    }, [])

    const handleUpdateDateParams = useCallback((startDate: string, endDate: string) => {
        const queryParams = new URLSearchParams(locationRef.current.search)

        // Parse dates in UTC to avoid timezone shifts when converting to ISO string
        if (startDate) {
            const startDateTime = dayjs.utc(startDate).startOf('day').toISOString()
            queryParams.set(OrderFilterKey.START_DATE, startDateTime)
        } else {
            queryParams.delete(OrderFilterKey.START_DATE)
        }

        if (endDate) {
            const endDateTime = dayjs.utc(endDate).endOf('day').toISOString()
            queryParams.set(OrderFilterKey.END_DATE, endDateTime)
        } else {
            queryParams.delete(OrderFilterKey.END_DATE)
        }

        const navigationOpt: { replace: boolean; state?: Record<string, boolean> } = {
            replace: true
        }

        if (locationRef.current?.state) {
            navigationOpt.state = locationRef.current.state
        }

        navigateRef.current(`${locationRef.current.pathname}?${queryParams.toString()}`, navigationOpt)
    }, [])

    const handleDateApply = useCallback((range: any, hours: any) => {
        if (!hours) {
            const [start, end] = range
            setRangeDates([dayjs(start), dayjs(end)])
            setFormattedRangeDates([
                dayjs(start).toISOString(),
                dayjs(end).toISOString()
            ])
        } else {
            const [start] = range
            const startDate = dayjs(start).utc().startOf('day').add(hours[0], 'hour')
            const endDate = dayjs(start).utc().startOf('day').add(hours[1], 'hour')

            setRangeDates([startDate, endDate])
            setFormattedRangeDates([
                startDate.toISOString(),
                endDate.toISOString()
            ])
        }
    }, [])

    const resetDateRange = useCallback(() => {
        const newEndDate = dayjs().utc()
        setRangeDates([newEndDate.subtract(30, 'days'), newEndDate])
        setFormattedRangeDates([null, null])
    }, [])

    const handleClearFilters = useCallback(() => {
        // Exclude IS_FREE from clear filters to preserve tab selection
        const filterKeysToReset = Object.values(OrderFilterKey).filter(key => key !== OrderFilterKey.IS_FREE)
        resetQueryParams(filterKeysToReset, locationRef.current, navigateRef.current)
        resetDateRange()
        setSelectedProjects([])
    }, [resetDateRange])

    return {
        filters,
        changeFilters,
        selectedProjects,
        setSelectedProjects,
        allowedPublishers,
        clearFiltersButton,
        handleUpdateParam,
        handleUpdateDateParams,
        handleClearFilters,
        URL_ARRAY_PARAM_SEPARATOR,
        rangeDates,
        isDatePickerOpen,
        setIsDatePickerOpen,
        formattedDate,
        handleDateApply,
        resetDateRange
    }
}
